/**
 * Labstep
 *
 * @module components/File/Table
 * @desc Table listing all Files
 */

import { EntityLink } from 'labstep-web/components/Entity/Link';
import { EntityTableParamsContainer as EntityTable } from 'labstep-web/components/Entity/Table';
import { FileActionMenu } from 'labstep-web/components/File/Action/Menu';
import { FilePreviewContainer as FilePreview } from 'labstep-web/components/File/Preview';
import { FileTypeImage } from 'labstep-web/components/File/TypeImage';
import { ICONS } from 'labstep-web/constants/icons';
import { Icon } from 'labstep-web/core/Icon';
import { Link } from 'labstep-web/core/Link';
import type { File } from 'labstep-web/models/file.model';
import { humanReadableDate } from 'labstep-web/services/date.service';
import { routing } from 'labstep-web/services/navigation';
import type { IRouting } from 'labstep-web/services/navigation.types';
import type React from 'react';
import styles from './styles.module.scss';
import type { FileNamePreviewProps, FileTableProps } from './types';

export const FileNamePreview: React.FC<FileNamePreviewProps> = ({
  file,
}) => (
  <FilePreview
    file={file}
    withDefault
    viewComponent={({ toggle }): React.ReactElement => (
      <div className={styles.nameContainer} onClick={toggle}>
        <div>
          <FileTypeImage file={file} size="mini" />
        </div>
        <div>{file.name}</div>
      </div>
    )}
  />
);

const extraColumns = [
  {
    header: 'Created on',
    content: (file: File): string =>
      humanReadableDate(file.created_at),
    sortKey: 'created_at',
    cellProps: { style: { flex: 1 } },
  },
  {
    header: 'File Type',
    content: (file: File): string =>
      file.extension ? file.extension.toUpperCase() : '-',
    sortKey: 'extension',
    cellProps: { style: { flex: 1 } },
  },
  {
    header: 'Size',
    content: (file: File): string => file.filesize,
    sortKey: 'extension',
    cellProps: { style: { flex: 1 } },
  },
  {
    header: 'Parent',
    content: (file: File): React.ReactElement => {
      const routeName =
        `${file.permission_entity_info?.entityName}_show` as keyof IRouting;
      const hasRoute =
        file.permission_entity_info?.entityName && routing[routeName];

      const content = (
        <>
          {file.permission_entity_info?.entityName &&
            file.permission_entity_info?.entityName in ICONS && (
              <Icon
                name={
                  ICONS[
                    file.permission_entity_info
                      ?.entityName as keyof typeof ICONS
                  ].primary
                }
              />
            )}
          {file.parent_name}
        </>
      );

      return hasRoute ? (
        <Link
          to={routeName}
          params={{ id: file.permission_entity_info?.id }}
        >
          {content}
        </Link>
      ) : (
        <span>{content}</span>
      );
    },
    cellProps: { style: { flex: 2 } },
  },
];

export const FileTable: React.FC<FileTableProps> = ({
  files,
  actions,
  ...rest
}) => (
  <EntityTable
    {...rest}
    id="files-table"
    primaryColumn={{
      header: '',
      content: (file): React.ReactElement => (
        <FileNamePreview file={file} />
      ),
      cellProps: { width: 4 },
    }}
    entities={files}
    extraColumns={extraColumns}
    actionMenu={({ entity }): React.ReactElement => (
      <FileActionMenu file={entity} actions={actions} />
    )}
    withCheckbox={false}
  />
);
